import { useCallback, useEffect, useRef, useState } from "react";

import { UseDragHandlerOptions, UseDragHandlerReturn } from "./types";

/**
 * Custom hook for handling mouse and touch drag interactions
 * Manages the drag lifecycle and global event listeners for smooth dragging
 *
 * @param options - Configuration options for drag behavior
 * @returns Object containing drag state and event handlers
 *
 * @example
 * ```tsx
 * const { isDragging, onMouseDown, onTouchStart } = useDragHandler({
 *   onDragStart: (clientX, clientY) => console.log('Drag started at', clientX, clientY),
 *   onDragMove: (clientX, clientY) => console.log('Dragging at', clientX, clientY),
 *   onDragEnd: () => console.log('Drag ended'),
 * });
 *
 * return <div onMouseDown={onMouseDown} onTouchStart={onTouchStart}>Drag me</div>;
 * ```
 */
const useDragHandler = ({
  enabled = true,
  onDragStart,
  onDragMove,
  onDragEnd,
}: UseDragHandlerOptions): UseDragHandlerReturn => {
  const [isDragging, setIsDragging] = useState(false);

  /** Store callbacks in refs to avoid re-registering event listeners */
  const onDragMoveRef = useRef(onDragMove);
  const onDragEndRef = useRef(onDragEnd);

  /** Update refs when callbacks change */
  useEffect(() => {
    onDragMoveRef.current = onDragMove;
  }, [onDragMove]);

  useEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);

  /**
   * Handle drag start
   */
  const handleStart = useCallback(
    (clientX: number, clientY: number) => {
      if (!enabled) return;
      setIsDragging(true);
      onDragStart?.(clientX, clientY);
    },
    [enabled, onDragStart],
  );

  /**
   * Handle drag move
   */
  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!enabled) return;
      onDragMoveRef.current?.(clientX, clientY);
    },
    [enabled],
  );

  /**
   * Handle drag end
   */
  const handleEnd = useCallback(() => {
    if (!enabled) return;
    setIsDragging(false);
    onDragEndRef.current?.();
  }, [enabled]);

  /**
   * Mouse event handlers
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleStart(e.clientX, e.clientY);
    },
    [handleStart],
  );

  /**
   * Touch event handlers
   */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length > 0) {
        handleStart(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    [handleStart],
  );

  /**
   * Global mouse move/up listeners
   * Only depends on isDragging to avoid re-registering listeners
   */
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleEnd();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMove, handleEnd]);

  /**
   * Global touch move/end listeners
   * Only depends on isDragging to avoid re-registering listeners
   */
  useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault(); // Prevent scrolling during drag on mobile
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleTouchEnd = () => {
      handleEnd();
    };

    // Use { passive: false } to allow preventDefault on touch events
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  return {
    isDragging,
    onMouseDown: handleMouseDown,
    onTouchStart: handleTouchStart,
  };
};

export { useDragHandler };
